// The page: the public guide (route, rules, practical info) and, behind a game link
// (/g/<token>), the shared scorecard. A team link keeps its own team's names and score (one per hole,
// for the team's shared glass); everyone sees every team live.
import { reportError } from './common/telemetry.ts';
import { GameSync, lastGame } from './sync.ts';
import { BONUS_HOLE, HOLES } from '../shared/course.ts';
import { MAX_NAME, MAX_STROKES, cleanName, fmtDiff, holeDone, holeScore, leaderboard, parseScore, playerNames, type GameState, type Op, type Team } from '../shared/game.ts';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const NUMBER_WORDS = ['Nul', 'Eén', 'Twee', 'Drie', 'Vier', 'Vijf', 'Zes', 'Zeven', 'Acht'];

const tokenMatch = /^\/g\/([A-Za-z0-9_-]{1,100})$/.exec(location.pathname);
const token = tokenMatch ? tokenMatch[1]! : null;
const TAB_KEY = token ? `pubgolf:tab:${token}` : 'pubgolf:tab';
const HOLE_KEY = token ? `pubgolf:hole:${token}` : 'pubgolf:hole';

function readPref(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------- route

function renderRoute(): void {
  $('holeList').innerHTML = HOLES.map(
    (h, i) => `<li class="hole${i === HOLES.length - 1 ? ' final' : ''}"><div class="flag">${i + 1}</div>
    <div><h3>${esc(h.name)}</h3><div class="time">${esc(h.time)} · ${esc(h.addr)}</div>
    <div class="drink"><strong>${esc(h.drink)}</strong><span class="par">par ${h.par}</span></div>
    <p class="note">${h.warn ? '<span class="warn">Let op: </span>' : ''}${esc(h.note)}
    <br><a href="${esc(h.maps)}" target="_blank" rel="noopener noreferrer">Kaart</a> &nbsp; <a href="tel:${esc(h.tel)}">Bellen</a></p></div></li>`,
  ).join('');
}

// ---------------------------------------------------------------- tabs

function selectTab(tab: string, scroll: boolean): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>('nav button[data-tab]');
  if (![...buttons].some((b) => b.dataset.tab === tab)) tab = 'route';
  buttons.forEach((b) => b.setAttribute('aria-selected', String(b.dataset.tab === tab)));
  document.querySelectorAll<HTMLElement>('main section').forEach((s) => (s.hidden = s.id !== tab));
  writePref(TAB_KEY, tab);
  if (scroll) window.scrollTo({ top: document.querySelector('nav')!.offsetTop, behavior: 'auto' });
}

document.querySelectorAll<HTMLButtonElement>('nav button[data-tab]').forEach((b) =>
  b.addEventListener('click', () => selectTab(b.dataset.tab!, true)),
);

// ---------------------------------------------------------------- scorecard

let sync: GameSync | null = null;
let hole = Math.min(Math.max(Number(readPref(HOLE_KEY) ?? 0) || 0, 0), HOLES.length - 1);
/** Values shown last time on the selected hole, to flash the ones someone else changed. */
let shown = new Map<string, string>();
let shownHole = -1;

function statusText(s: GameSync): { text: string; cls: string } {
  if (s.status === 'gone') return { text: 'Spel bestaat niet', cls: 'gone' };
  if (s.status === 'live') return { text: s.waiting ? '● Live · opslaan…' : '● Live', cls: 'live' };
  if (s.status === 'offline') return { text: s.waiting ? `Offline · ${s.waiting} wachten` : 'Offline', cls: 'offline' };
  return { text: 'Verbinden…', cls: '' };
}

/** True while a change typed into a field is applied: that field already shows it. */
let typedChange = false;
/** True while the team cards are replaced: a field removed then is not being left by the player. */
let drawingCards = false;

/**
 * Draws the scorecard. After a change typed into a field (`typedChange`), the team cards are left as
 * they are: redrawing them would replace the field the player just tapped next, and lose its focus.
 */
function render(remote: boolean): void {
  if (!sync) return;
  const status = $('status');
  const st = statusText(sync);
  status.hidden = false;
  status.textContent = st.text;
  status.className = `status ${st.cls}`;

  const view = sync.view();
  const gone = sync.status === 'gone';
  $('game').hidden = gone || !view;
  $('gameMsg').hidden = !gone && !!view;
  if (gone) {
    $('gameMsgTitle').textContent = 'Dit spel bestaat niet (meer)';
    $('gameMsgText').textContent = 'Vraag de organisator om de juiste link. De route, regels en praktische info staan in de andere tabs.';
    $('playerStat').hidden = true;
    return;
  }
  if (!view) {
    $('gameMsgTitle').textContent = sync.status === 'offline' ? 'Geen verbinding' : 'Spel laden…';
    $('gameMsgText').textContent =
      sync.status === 'offline' ? 'Deze spellink is op dit toestel nog niet geopend. Open hem opnieuw zodra je bereik hebt.' : 'Een moment.';
    return;
  }

  const players = view.teams.reduce((n, t) => n + t.players.length, 0);
  $('playerCount').textContent = String(players);
  $('playerStat').hidden = false;
  $('teamCount').textContent = NUMBER_WORDS[view.teams.length] ?? String(view.teams.length);

  const banner = $('banner');
  if (sync.status === 'offline') {
    banner.hidden = false;
    banner.className = 'banner';
    banner.textContent = 'Geen verbinding. Je scores blijven op dit toestel en gaan vanzelf mee zodra er weer bereik is.';
  } else banner.hidden = true;

  const myTeam = sync.team;
  $('scoreLead').textContent =
    myTeam === null
      ? 'Je kijkt mee. Elk team deelt een glas en houdt per hole één teamscore bij.'
      : 'Tik op een hole en vul de score van je team in: alle slokken uit jullie glas, bij elkaar opgeteld.';
  $('hint').textContent =
    myTeam === null
      ? 'Met deze link kijk je alleen mee. Tellen gaat met de link van je team.'
      : `Je telt voor ${view.teams[myTeam]?.name ?? 'je team'}; de andere teams zie je live meelopen. Spreek per team één teller af.`;

  renderBoard(view, myTeam);
  renderPicker(view);
  if (!typedChange) renderHole(view, myTeam, remote);
  else if (shownHole === hole) view.teams.forEach((t, ti) => shown.set(String(ti), shownScore(t)));
}

function renderBoard(view: GameState, myTeam: number | null): void {
  $('board').innerHTML = leaderboard(view)
    .map(
      (r, k) => `<div class="board-row${k === 0 && r.played ? ' lead1' : ''}">
      <span class="pos">${k + 1}</span><span class="board-team">${esc(r.team.name)}${r.index === myTeam ? '<span class="mine">jouw team</span>' : ''}<span class="roster">${esc(playerNames(r.team) || 'nog geen namen')}</span></span>
      <span class="tot">${r.played ? r.tot : '–'}</span>
      <span class="vs">${r.played ? fmtDiff(r.diff) : 'nog niet'}</span>
    </div>`,
    )
    .join('');
}

function renderPicker(view: GameState): void {
  $('picker').innerHTML = HOLES.map(
    (_, i) => `<button aria-pressed="${i === hole}" class="${holeDone(view, i) ? 'done' : ''}" data-h="${i}" aria-label="Hole ${i + 1}">${i + 1}</button>`,
  ).join('');
}

function renderHole(view: GameState, myTeam: number | null, remote: boolean): void {
  const H = HOLES[hole]!;
  $('holeTitle').textContent = `${hole + 1}. ${H.name}`;
  $('holePar').textContent = `${H.drink}, par ${H.par}`;

  // Keep a name or score being typed (and the caret) when someone else's change redraws the cards.
  const active = document.activeElement;
  let typing: { selector: string; value: string; start: number | null; end: number | null } | null = null;
  if (active instanceof HTMLInputElement && active.type === 'text' && $('teams').contains(active)) {
    const selector =
      active.dataset.player !== undefined
        ? `input[data-team="${active.dataset.team}"][data-player="${active.dataset.player}"]`
        : active.dataset.score !== undefined
          ? `input[data-score="${active.dataset.score}"]`
          : `input.team-name[data-team="${active.dataset.team}"]`;
    typing = { selector, value: active.value, start: active.selectionStart, end: active.selectionEnd };
  }

  const order = view.teams.map((_, i) => i);
  if (myTeam !== null && myTeam < order.length) order.sort((a, b) => (a === myTeam ? -1 : b === myTeam ? 1 : a - b));
  const flash = remote && shownHole === hole;
  const next = new Map<string, string>();

  const cards = order
    .map((ti) => {
      const t = view.teams[ti]!;
      const mine = ti === myTeam;
      const s = holeScore(t, hole);
      const entered = t.scores[hole] ?? null;
      const key = String(ti);
      const shownValue = shownScore(t);
      next.set(key, shownValue);
      const flashCls = flash && shown.has(key) && shown.get(key) !== shownValue ? ' flash' : '';
      const bonus =
        hole === BONUS_HOLE
          ? mine
            ? `<label class="bonus"><input type="checkbox" data-bonus="${ti}" ${t.bonus ? 'checked' : ''}> Als eerste de deur open (−1)</label>`
            : t.bonus
              ? '<div class="bonus readonly">✓ Als eerste de deur open (−1)</div>'
              : ''
          : '';
      if (!mine) {
        return `<div class="team readonly">
      <div class="team-top"><span class="team-name">${esc(t.name)}</span><span class="team-hole${flashCls}">${shownValue} <span class="vs">/ ${H.par}</span></span></div>
      <p class="roster">${esc(playerNames(t) || 'nog geen namen')}</p>${bonus}
    </div>`;
      }
      const names = t.players
        .map(
          (n, pi) =>
            `<input type="text" class="pname" data-team="${ti}" data-player="${pi}" value="${esc(n)}" placeholder="Naam speler ${pi + 1}" maxlength="${MAX_NAME}" aria-label="Naam speler ${pi + 1}" autocomplete="off">`,
        )
        .join('');
      const counts = hole === BONUS_HOLE && t.bonus && s !== null ? ` · telt als ${s}` : '';
      return `<div class="team mine">
      <div class="team-label">Jouw team</div>
      <div class="team-top"><input type="text" class="team-name" data-team="${ti}" value="${esc(t.name)}" maxlength="${MAX_NAME}" aria-label="Teamnaam" autocomplete="off"></div>
      <div class="team-label">Spelers</div>
      <div class="names">${names}</div>
      <div class="glass">
        <span class="glass-label">Teamscore hole ${hole + 1}<small>par ${H.par}${counts}</small></span>
        <div class="step">
          <button data-act="-" data-team="${ti}" aria-label="Eén slag minder">−</button>
          <input type="text" class="score${flashCls}" data-score="${ti}" value="${entered ?? ''}" placeholder="·" inputmode="numeric" pattern="[0-9]*" maxlength="2" aria-label="Teamscore hole ${hole + 1}" autocomplete="off">
          <button data-act="+" data-team="${ti}" aria-label="Eén slag meer">+</button>
        </div>
      </div>${bonus}
    </div>`;
    })
    .join('');
  drawingCards = true;
  try {
    $('teams').innerHTML = cards;
  } finally {
    drawingCards = false;
  }
  shown = next;
  shownHole = hole;

  if (typing) {
    const input = $('teams').querySelector<HTMLInputElement>(typing.selector);
    if (input) {
      input.value = typing.value;
      input.focus({ preventScroll: true });
      try {
        input.setSelectionRange(typing.start, typing.end);
      } catch {
        // ignore
      }
    }
  }
}

function shownScore(team: Team): string {
  const s = holeScore(team, hole);
  return s === null ? '–' : String(s);
}

/** Saves a name or score field when it differs from the game. A score that is not 1-99 is put back. */
function commitField(el: HTMLInputElement): void {
  const team = sync?.view()?.teams[Number(el.dataset.team ?? el.dataset.score)];
  if (!team) return;
  if (el.dataset.score !== undefined) {
    const v = parseScore(el.value);
    const cur = team.scores[hole] ?? null;
    if (v === undefined) el.value = cur === null ? '' : String(cur);
    else if (v !== cur) typed({ t: 'score', team: Number(el.dataset.score), hole, v });
  } else if (el.dataset.player !== undefined) {
    const player = Number(el.dataset.player);
    if (cleanName(el.value, '') !== team.players[player]) typed({ t: 'player', team: Number(el.dataset.team), player, name: el.value });
  } else if (el.dataset.team !== undefined) {
    if (cleanName(el.value, 'Team') !== team.name) typed({ t: 'team', team: Number(el.dataset.team), name: el.value });
  }
}

/** Applies a change typed into a field. */
function typed(op: Op): void {
  typedChange = true;
  try {
    sync?.change(op);
  } finally {
    typedChange = false;
  }
}

function startGame(t: string): void {
  sync = new GameSync(t, (remote) => {
    try {
      render(remote);
    } catch (error) {
      reportError(error, { where: 'render' });
    }
  });
  // Installing from a game link opens that game from the home screen.
  ($('manifest') as HTMLLinkElement).href = `/manifest.webmanifest?g=${encodeURIComponent(t)}`;
  $('noGame').hidden = true;

  $('picker').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-h]');
    if (!b) return;
    hole = Number(b.dataset.h);
    writePref(HOLE_KEY, String(hole));
    render(false);
  });
  $('teams').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!b || !sync) return;
    const team = Number(b.dataset.team);
    const cur = sync.view()?.teams[team]?.scores[hole] ?? null;
    const v = b.dataset.act === '+' ? (cur === null ? 1 : Math.min(cur + 1, MAX_STROKES)) : cur === null || cur <= 1 ? null : cur - 1;
    sync.change({ t: 'score', team, hole, v });
  });
  $('teams').addEventListener('change', (e) => {
    const el = e.target as HTMLInputElement;
    if (sync && el.dataset.bonus !== undefined) sync.change({ t: 'bonus', team: Number(el.dataset.bonus), v: el.checked });
  });
  // Names and scores are saved when the field is left (another field, a button, Enter). Not on
  // "change": a field restored by a redraw (someone else's change while typing) does not fire it.
  $('teams').addEventListener('focusout', (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.type === 'text' && el.isConnected && !drawingCards) commitField(el);
  });
  // Tapping the score selects it, so a total can be typed straight over it.
  $('teams').addEventListener('focusin', (e) => {
    const el = e.target as HTMLElement;
    if (el instanceof HTMLInputElement && el.dataset.score !== undefined) el.select();
  });
  // Enter closes the keyboard and saves the name or score.
  $('teams').addEventListener('keydown', (e) => {
    const el = e.target as HTMLElement;
    if (e.key === 'Enter' && el instanceof HTMLInputElement && el.type === 'text') el.blur();
  });

  render(false);
  sync.start();
}

function showNoGame(): void {
  $('noGame').hidden = false;
  const last = lastGame();
  if (last && /^[A-Za-z0-9_-]{1,100}$/.test(last.token)) {
    const link = $<HTMLAnchorElement>('resumeLink');
    link.href = `/g/${last.token}`;
    link.textContent = `Verder met ${last.name}`;
    $('resume').hidden = false;
  }
}

// ---------------------------------------------------------------- start

renderRoute();
selectTab(readPref(TAB_KEY) ?? 'route', false);
if (token) startGame(token);
else showNoGame();

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch((error: unknown) => reportError(error, { where: 'sw-register' }));
}
